import React, { useRef } from 'react';
import { ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';

export type StepBlock =
  | { type: 'text'; text: string }
  | { type: 'code'; language?: string; code: string }
  | { type: 'link'; label?: string; url: string }
  | { type: 'image'; src: string; alt: string; caption?: string; display?: 'contain' | 'cover'; allow_zoom?: boolean }
  | { type: 'video'; src: string; poster?: string; caption?: string; transcript?: string; allow_speed_control?: boolean };

interface BlockEditorProps {
  blocks: StepBlock[];
  onChange: (blocks: StepBlock[]) => void;
}

interface TextEditResult {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

const wrapSelection = (
  text: string,
  selectionStart: number,
  selectionEnd: number,
  before: string,
  after: string,
  placeholder: string,
): TextEditResult => {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const selected = text.slice(start, end);
  const content = selected || placeholder;
  const nextText = `${text.slice(0, start)}${before}${content}${after}${text.slice(end)}`;
  const contentStart = start + before.length;
  return {
    text: nextText,
    selectionStart: contentStart,
    selectionEnd: contentStart + content.length,
  };
};

const getLineBounds = (text: string, selectionStart: number, selectionEnd: number) => {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const lineStart = text.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const endIndex = end < text.length ? text.indexOf('\n', end) : -1;
  const lineEnd = endIndex === -1 ? text.length : endIndex;
  return { lineStart, lineEnd };
};

const prefixSelectedLines = (
  text: string,
  selectionStart: number,
  selectionEnd: number,
  prefixer: (line: string, index: number) => string,
): TextEditResult => {
  const { lineStart, lineEnd } = getLineBounds(text, selectionStart, selectionEnd);
  const segment = text.slice(lineStart, lineEnd);
  const lines = segment.split('\n');
  const updatedLines = lines.map((line, idx) => {
    if (!line.trim()) return line;
    return prefixer(line, idx);
  });
  const nextSegment = updatedLines.join('\n');
  return {
    text: `${text.slice(0, lineStart)}${nextSegment}${text.slice(lineEnd)}`,
    selectionStart: lineStart,
    selectionEnd: lineStart + nextSegment.length,
  };
};


export const BlockEditor: React.FC<BlockEditorProps> = ({ blocks, onChange }) => {
  const textAreaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});

  const moveBlock = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= blocks.length) return;
    const updated = [...blocks];
    const [moved] = updated.splice(index, 1);
    updated.splice(nextIndex, 0, moved);
    onChange(updated);
  };

  const deleteBlock = (index: number) => {
    onChange(blocks.filter((_, idx) => idx !== index));
  };

  const updateTextBlock = (index: number, nextText: string) => {
    onChange(
      blocks.map((item, idx) =>
        idx === index ? { ...item, type: 'text', text: nextText } : item
      )
    );
  };

  const applyTextEdit = (index: number, editFn: (value: string, start: number, end: number) => TextEditResult) => {
    const block = blocks[index];
    if (!block || block.type !== 'text') return;
    const textarea = textAreaRefs.current[index];
    const text = String(block.text || '');
    const selectionStart = textarea ? textarea.selectionStart : text.length;
    const selectionEnd = textarea ? textarea.selectionEnd : text.length;
    const result = editFn(text, selectionStart, selectionEnd);
    updateTextBlock(index, result.text);
    window.setTimeout(() => {
      const nextArea = textAreaRefs.current[index];
      if (!nextArea) return;
      nextArea.focus();
      nextArea.setSelectionRange(result.selectionStart, result.selectionEnd);
    }, 0);
  };

  return (
    <div className="space-y-4">
      {blocks.map((block, index) => (
        <div key={`block-${index}`} className={`rounded-lg border bg-white p-4 ${block.type === 'image' || block.type === 'video' ? 'border-indigo-300 ring-1 ring-indigo-100' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">{block.type}</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => moveBlock(index, -1)}
              >
                <ChevronUpIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => moveBlock(index, 1)}
              >
                <ChevronDownIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="text-gray-400 hover:text-red-500"
                onClick={() => deleteBlock(index)}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {block.type === 'text' && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-1.5 rounded border border-gray-200 bg-gray-50 p-2">
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => wrapSelection(value, start, end, '**', '**', 'bold text'))}
                >
                  Bold
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => wrapSelection(value, start, end, '*', '*', 'italic text'))}
                >
                  Italic
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => prefixSelectedLines(value, start, end, (line) => (line.startsWith('## ') ? line : `## ${line}`)))}
                >
                  H2
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => prefixSelectedLines(value, start, end, (line) => (line.startsWith('- ') ? line : `- ${line}`)))}
                >
                  Bullets
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => prefixSelectedLines(value, start, end, (line, idx) => `${idx + 1}. ${line.replace(/^\d+\.\s+/, '')}`))}
                >
                  Numbered
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => prefixSelectedLines(value, start, end, (line) => (line.startsWith('> ') ? line : `> ${line}`)))}
                >
                  Quote
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => wrapSelection(value, start, end, '`', '`', 'code'))}
                >
                  Inline Code
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => wrapSelection(value, start, end, '\n```text\n', '\n```\n', 'code here'))}
                >
                  Code Block
                </button>
                <button
                  type="button"
                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
                  onClick={() => applyTextEdit(index, (value, start, end) => wrapSelection(value, start, end, '[', '](https://)', 'link text'))}
                >
                  Link
                </button>
              </div>
              <textarea
                ref={(el) => {
                  textAreaRefs.current[index] = el;
                }}
                className="w-full border rounded px-3 py-2 text-sm"
                rows={6}
                value={block.text}
                onChange={(e) => updateTextBlock(index, e.target.value)}
                placeholder="Student-facing text (supports Markdown)"
              />
              <p className="text-xs text-gray-500">Formatting toolbar inserts Markdown. Students see rendered formatting in lesson view.</p>
            </div>
          )}

          {block.type === 'code' && (
            <div className="space-y-3">
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={block.language || ''}
                onChange={(e) =>
                  onChange(
                    blocks.map((item, idx) =>
                      idx === index
                        ? {
                            ...item,
                            type: 'code',
                            language: e.target.value,
                            code: item.type === 'code' ? item.code : '',
                          }
                        : item
                    )
                  )
                }
              >
                <option value="">Language (optional)</option>
                <option value="python">Python</option>
                <option value="javascript">JavaScript</option>
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="sql">SQL</option>
                <option value="text">Plain text</option>
              </select>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm font-mono"
                rows={6}
                value={block.code}
                onChange={(e) =>
                  onChange(
                    blocks.map((item, idx) =>
                      idx === index
                        ? {
                            ...item,
                            type: 'code',
                            code: e.target.value,
                            language: item.type === 'code' ? item.language : '',
                          }
                        : item
                    )
                  )
                }
                placeholder="Paste code for students"
              />
            </div>
          )}

          {block.type === 'link' && (
            <div className="space-y-3">
              <input
                className="w-full border rounded px-3 py-2 text-sm"
                value={block.label || ''}
                onChange={(e) =>
                  onChange(
                    blocks.map((item, idx) =>
                      idx === index
                        ? {
                            ...item,
                            type: 'link',
                            label: e.target.value,
                            url: item.type === 'link' ? item.url : '',
                          }
                        : item
                    )
                  )
                }
                placeholder="Link label (optional)"
              />
              <input
                className="w-full border rounded px-3 py-2 text-sm"
                value={block.url}
                onChange={(e) =>
                  onChange(
                    blocks.map((item, idx) =>
                      idx === index
                        ? {
                            ...item,
                            type: 'link',
                            url: e.target.value,
                            label: item.type === 'link' ? item.label : '',
                          }
                        : item
                    )
                  )
                }
                placeholder="https://"
              />
            </div>
          )}

          {block.type === 'image' && (
            <div className="space-y-3">
              <p className="text-xs text-indigo-700">Image block — student-facing visual</p>
              <input className="w-full border rounded px-3 py-2 text-sm" value={block.src} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'image' ? { ...item, src: e.target.value } : item))} placeholder="HTTPS image URL" />
              <textarea className="w-full border rounded px-3 py-2 text-sm" rows={2} value={block.alt} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'image' ? { ...item, alt: e.target.value } : item))} placeholder="Meaningful alt text (required)" />
              <input className="w-full border rounded px-3 py-2 text-sm" value={block.caption || ''} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'image' ? { ...item, caption: e.target.value } : item))} placeholder="Caption (optional)" />
              <div className="flex flex-wrap gap-4">
                <label className="text-sm text-gray-700">Display
                  <select className="ml-2 rounded border px-2 py-1" value={block.display || 'contain'} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'image' ? { ...item, display: e.target.value as 'contain' | 'cover' } : item))}>
                    <option value="contain">Contain</option>
                    <option value="cover">Cover</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={block.allow_zoom !== false} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'image' ? { ...item, allow_zoom: e.target.checked } : item))} />
                  Allow enlargement
                </label>
              </div>
            </div>
          )}

          {block.type === 'video' && (
            <div className="space-y-3">
              <p className="text-xs text-indigo-700">Video block — accessible HTML5 player</p>
              <input className="w-full border rounded px-3 py-2 text-sm" value={block.src} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'video' ? { ...item, src: e.target.value } : item))} placeholder="HTTPS video URL" />
              <input className="w-full border rounded px-3 py-2 text-sm" value={block.poster || ''} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'video' ? { ...item, poster: e.target.value } : item))} placeholder="HTTPS poster URL (optional)" />
              <input className="w-full border rounded px-3 py-2 text-sm" value={block.caption || ''} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'video' ? { ...item, caption: e.target.value } : item))} placeholder="Caption (optional)" />
              <input className="w-full border rounded px-3 py-2 text-sm" value={block.transcript || ''} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'video' ? { ...item, transcript: e.target.value } : item))} placeholder="HTTPS transcript URL (optional)" />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={block.allow_speed_control === true} onChange={(e) => onChange(blocks.map((item, idx) => idx === index && item.type === 'video' ? { ...item, allow_speed_control: e.target.checked } : item))} />
                Show playback-speed control
              </label>
            </div>
          )}
        </div>
      ))}

      {blocks.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
          Add a block to start building the student view.
        </div>
      )}
    </div>
  );
};
